import { cn } from "@/lib/utils";
import { countryFlagSrc, countryFlagEmoji } from "@/lib/country-flags";
import { getNflTeam } from "@/config/nfl-teams";
import { NflHelmet } from "./NflHelmet";

/**
 * Standard flag size scale (square tile side, in px). This is the single source
 * of truth for flag sizing — prefer a token over a raw pixel value so flags stay
 * consistent across the app.
 *
 *   sm — dense rows (spread/total selectors)
 *   md — default; inline team labels, pick buttons, card headers
 *   lg — emphasis (e.g. popovers, summaries)
 *
 * The artwork is gold country-map silhouettes (each a unique outline), so every
 * flag is rendered inside an identical square tile with `object-contain` — that
 * way they occupy equal space and read as uniform badges despite the differing
 * map shapes.
 */
export const FLAG_SIZES = { sm: 24, md: 28, lg: 40 } as const;
export type FlagSize = keyof typeof FLAG_SIZES;

function resolveSize(size: FlagSize | number): number {
  return typeof size === "number" ? size : FLAG_SIZES[size];
}

/**
 * NFL helmets are transparent line art with no tile chrome, so they render
 * larger than the square flag tiles to carry equal visual weight.
 */
const NFL_HELMET_SCALE = 1.3;

/**
 * Whether a name resolves to artwork (NFL helmet or country flag).
 *
 * Exported so a caller can reserve the icon slot rather than let the row
 * reflow when it doesn't. CountryFlag deliberately renders nothing for an
 * unknown name, which is right in most places — but in a list where SOME rows
 * have art, the missing element collapses the flex gap and the text shifts
 * left, reading as misalignment rather than absence.
 */
/**
 * Width a row should reserve for its icon so every row's text starts at the
 * same x, whatever art lands in the slot.
 *
 * Sized to the LARGEST art at this size — the NFL helmet, which renders 1.3x
 * the flag tile. A slot sized to the flag instead would leave helmet rows 8px
 * wider than the rest, which is the stagger this exists to prevent.
 */
export function teamArtSlotPx(size: FlagSize | number = 'md'): number {
  return Math.round(resolveSize(size) * NFL_HELMET_SCALE);
}

export function hasTeamArt(name: string | null | undefined): boolean {
  return Boolean(getNflTeam(name) ?? countryFlagSrc(name));
}

interface CountryFlagProps {
  /** Team/country name as it appears in event data (e.g. "Argentina", "USA"). */
  name: string | null | undefined;
  /** Size token ('sm' | 'md' | 'lg') or a raw height in px. Default 'md'. */
  size?: FlagSize | number;
  /**
   * 'badge' (default) renders the gold silhouette tile; 'emoji' renders the
   * emoji flag (e.g. 🇨🇿), falling back to the badge if no emoji exists.
   */
  variant?: "badge" | "emoji";
  /**
   * NFL helmets only: which way the helmet points. Matchup rows pass 'right'
   * for the away/left side and 'left' for the home/right side so the helmets
   * face each other. Ignored for country flags.
   */
  facing?: "left" | "right";
  className?: string;
}

/**
 * Renders a small country flag for recognized country names, or nothing at all
 * when the name isn't a known country — so it's safe to drop next to any team
 * name regardless of sport.
 */
export function CountryFlag({
  name,
  size = "md",
  variant = "badge",
  facing = "left",
  className,
}: CountryFlagProps) {
  // NFL team names (seeded as city display names) render a color-coded
  // helmet instead of a country flag. Checked first because these names are
  // never countries. Known limitation: an NCAA team seeded as a bare city
  // name (e.g. "Washington") would also match — acceptable until a
  // sport-aware TeamBadge exists.
  const nflTeam = getNflTeam(name);
  if (nflTeam) {
    return (
      <NflHelmet
        team={nflTeam}
        size={Math.round(resolveSize(size) * NFL_HELMET_SCALE)}
        facing={facing}
        className={className}
      />
    );
  }

  const src = countryFlagSrc(name);
  if (!src) return null;

  const px = resolveSize(size);

  if (variant === "emoji") {
    const emoji = countryFlagEmoji(name);
    if (emoji) {
      return (
        <span
          aria-hidden="true"
          style={{ fontSize: Math.round(px * 0.75), lineHeight: 1 }}
          className={cn("inline-block shrink-0 align-middle", className)}
        >
          {emoji}
        </span>
      );
    }
  }

  return (
    <span
      style={{ width: px, height: px }}
      className={cn(
        "inline-flex items-center justify-center shrink-0 align-middle rounded-[4px] bg-cream/6 ring-1 ring-cream/15 p-px",
        className,
      )}
    >
      <img
        src={src}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="max-h-full max-w-full object-contain"
      />
    </span>
  );
}
